import { AdequacySharedProjectContextProps } from "../adequacySharedProjectContextProps";
import AdequacyFilters from "../network-assessment/adequacy/adequacyFilters";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { ZipCodeAnalysisStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import ProjectRunConfiguration from "../projectRunConfiguration";
import Project from "../project";
import { CalculationMethodEnum, TemplateMetadata } from "@app/templates/templateModels";
import { ZipClassTrendChartItem, ZipCodeTrendChartItem } from "./interfaces/zipTrendChartItems";
import { JSX } from "react";
import { ChangedGapsEnum } from "@app/projects/enumerators";

export interface ZipContextInProps {
  children: React.ReactNode;
}

export type ZipAnalysisSearchSelections = ZipClassAnalysisSearchSelections & ZipCodeAnalysisSearchSelections;
export type ZipAnalysisSearchOptions = ZipClassAnalysisSearchOptions & ZipCodeAnalysisSearchOptions;
export type ZipAnalysisSearchParameters = ZipClassAnalysisSearchParameters & ZipCodeAnalysisSearchParameters;

export interface ZipContextOutProps {
  dispatch: React.Dispatch<ZipProjectAction>;
  getSearchParametersFromFilterSelections: (filterSelections: ZipAnalysisSearchSelections) => ZipAnalysisSearchParameters;
  onChangeChangedGapsFilter: (newChangedGapsFilter: ChangedGapsEnum) => void;
  onChangeCompareToRunConfiguration: (newCompareToRunConfigurationId: number) => void;
  onChangePerspectiveToZipClass: (project?: Project, runConfiguration?: ProjectRunConfiguration) => void;
  onChangePerspectiveToZipCode: (project?: Project, runConfiguration?: ProjectRunConfiguration) => void;
  onChangeProjectStatus: (payload: ZipCodeAnalysisStatusChangeNotificationMessagePayload) => void;
  onChangeRunConfiguration: (newRunConfigurationId: number) => void;
  onChangeSelectedMemberships: (newMemberships: DropdownItem<string>[]) => void;
  onEditTimeslicePublishComment: () => void;
  onLoadPage: (projectId: string) => void;
  onApplyFilters: () => void;
  onResetStatusBanners: (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean) => void;
  onResetFilters: () => void;
  onRunProject: (projectId: number) => void;
  onProcessProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  onPublishTimeSlice: (comment?: string) => Promise<void>;
  onSubmitEditProject: (projectId: number) => void;
  onChangeTablePage: (newSkip: number) => void;
  onSortTable: (column: keyof (ZipCodeTableData & ZipClassTableData), direction: SortDirections) => void;
  onClickSpecialtyGroupOrZipCode: (zipCode: string, specialtyGroupCode: string) => void;
  state: ZipContextState;
}

export interface ZipContextState extends AdequacySharedProjectContextProps {
  adequacyFilters: AdequacyFilters;
  calculationMethod: CalculationMethodEnum;
  calculationMethodCompared: CalculationMethodEnum;
  dualMembershipRunConfiguration: ProjectRunConfiguration;
  filterOptions: ZipAnalysisSearchOptions;
  filterSelections: ZipAnalysisSearchSelections;
  hasExpandableSpecialties: boolean;
  isCompareMode: boolean;
  isDrilledIn: boolean;
  isDualMembershipMode: boolean;
  processProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  projectHasUnprocessedProjectionProviders: boolean;
  regionSetIds: number[],
  regionSetSpecialtyGroupToPartsLookup: {
    [regionSetSpecialtyGroupId: string]: {
      regionSetId: number;
      specialtyGroupCode: string;
      zipCode: string;
    }
  };
  selectedRows: string[];
  setIsDualMembershipMode: (is: boolean) => void;
  setProjectHasUnprocessedProjectionProviders: (has: boolean) => void;
  setProjectProjectionFlags: (hasUnprocessedProviderTerminations: boolean, hasUnprocessedProjectionProviders: boolean) => void;
  setShouldRefreshMarketProviders: (should: boolean) => void;
  setShouldRefreshNetNewProviders: (should: boolean) => void;
  setShouldRefreshNetworkProviders: (should: boolean) => void;
  setShouldRefreshProspectProviders: (should: boolean) => void;
  shouldRefreshMarketProviders: boolean;
  shouldRefreshNetNewProviders: boolean;
  shouldRefreshNetworkProviders: boolean;
  shouldRefreshProspectProviders: boolean;
  shouldShowSummariesTab: boolean;
  skip: number;
  sortOption: ZipAnalysisSortOption;
  take: number;
  templateMetadataCompared: TemplateMetadata;
  trendChartData: ZipClassTrendChartItem[] | ZipCodeTrendChartItem[];
  totalNumberOfRecords: number;
  urlPathnameString: string;
  urlSearchString: string;
  zipClassDrillInDetails: ZipClassAggregateResultDisplayModel[];
  zipDetails: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
  zipDetailsCompared: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
  zipExpansions: Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>;
  zipExpansionsCompared: Map<string, ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[]>;
}

export enum ZipProjectActionsEnum {
  ChangeChangedGapsFilter = "ChangeChangedGapsFilter",
  ChangeCheckbox = "ChangeCheckbox",
  ChangeFilter = "ChangeFilter",
  ChangeMemberships = "ChangeMemberships",
  ChangePage = "ChangePage",
  ChangePerspectiveZipClass = "ChangePerspectiveZipClass",
  ChangePerspectiveZipCode = "ChangePerspectiveZipCode",
  ChangeCompareToProjectRunConfiguration = "ChangeCompareToProjectRunConfiguration",
  ChangeProjectRunConfiguration = "ChangeProjectRunConfiguration",
  // this is triggered by signalr notifications, not the user
  ChangeProjectStatus = "ChangeProjectStatus",
  ClickSpecialtyGroupOrZipCode = "ClickZipOrSpecialtyGroupCode",
  EditProject = "EditProject",
  EditTimeslicePublishComment = "EditTimeslicePublishComment",
  EndRunProject = "EndRunProject",
  Error = "Error",
  Loading = "Loading",
  LoadProjectWithoutRunConfiguration = "LoadProjectWithoutRunConfiguration",
  NavigateHistory = "NavigateHistory",
  ProcessProviderChanges = "ProcessProviderChanges",
  PublishProjectRunConfiguration = "PublishProjectRunConfiguration",
  RenderedNetworkProviders = "RenderedNetworkProviders",
  ResetFilters = "ResetFilters",
  ResetStatusBanners = "ResetStatusBanners",
  SortTable = "SortTable",
  StartRunProject = "StartRunProject",
  ApplyFilters = "SubmitFilters",
}

export interface ZipProjectAction {
  type: ZipProjectActionsEnum;
  payload: Partial<ZipContextState>;
}

export interface ZipProjectRouteComponentProps {
  projectId: string;
}

/**
 * The model used to retrieve zip analysis results for an entire project,
 * as opposed to a single regionSetSpecialtyGroup. 
 * The run configuration ids are both only for compare mode.
 */
export interface ZipAnalysisQueryParameters {
  limit?: number;
  offset?: number;
  searchParameters: ZipCodeAnalysisSearchParameters | ZipClassAnalysisSearchParameters;
  sortOptions: ZipAnalysisSortOption[];
}

export interface ComparingZipAnalysisQueryParameters {
  projectRunConfigurationId1: number;
  projectRunConfigurationId2: number;
  parameters: ZipAnalysisQueryParameters;
  changedGaps: ChangedGapsEnum;
  getGaps: boolean;
}

export interface ZipAnalysisSortOption {
  column: ZipCodeSortableColumnEnum | ZipClassSortableColumnEnum;
  direction: SortDirections;
}

// This needs to match the API's ZipCodeAnalysisSortableColumnEnum
// the numbers are just for easy debugging
export enum ZipCodeSortableColumnEnum {
  Unknown = 0,
  StateName = 1,
  County = 2,
  City = 3,
  ZipClass = 4,
  ZipCode = 5,
  SpecialtyGroupCode = 6,
  MinProviderCount = 7,
  ReqDist = 8,
  ReqTime = 9,
  MinPassPercent = 10,
  MetPassPercent = 11,
  MembershipCount = 12,
  ProviderCount = 13,
  MemberCountWithAccess = 14,
  WithAccessPercent = 15,
  MemberCountWithoutAccess = 16,
  WithoutAccessPercent = 17,
  AverageDistance = 18,
  MaximumDistance = 19,
  AverageTime = 20,
  MaximumTime = 21,
  AverageDistance2 = 22,
  AverageDistanceWithAccess = 23,
  AverageDistanceWithAccess2 = 24,
  AverageDistanceWithoutAccess = 25,
  AverageDistanceWithoutAccess2 = 26,
  AverageTime2 = 27,
  AverageTimeWithAccess = 28,
  AverageTimeWithAccess2 = 29,
  AverageTimeWithoutAccess = 30,
  AverageTimeWithoutAccess2 = 31,
  MaximumDistance2 = 32,
  MaximumDistanceWithAccess = 33,
  MaximumDistanceWithAccess2 = 34,
  MaximumDistanceWithoutAccess = 35,
  MaximumDistanceWithoutAccess2 = 36,
  MaximumTime2 = 37,
  MaximumTimeWithAccess = 38,
  MaximumTimeWithAccess2 = 39,
  MaximumTimeWithoutAccess = 40,
  MaximumTimeWithoutAccess2 = 41,
  MetTime = 42,
  MetDist = 43,
  WithAccessByDistancePercent = 44,
  WithAccessByTimePercent = 45,
  MarketAccessPercent = 46,
  MarketServicing = 47,
  MarketPercent = 48,
  MembershipType = 49,
}

/* This needs to match the API's ZipClassAggregateSortableColumnEnum */
export enum ZipClassSortableColumnEnum {
  Unknown = 0,
  ZipClass = 1,
  SpecialtyGroupCode = 2,
  MinProviderCount = 3,
  ReqDist = 4,
  ReqTime = 5,
  MinPassPercent = 6,
  MembershipCount = 7,
  ProviderCount = 8,
  MemberCountWithAccess = 9,
  WithAccessPercent = 10,
  MemberCountWithoutAccess = 11,
  WithoutAccessPercent = 12,
  MetOverall = 13,
  AverageDistance = 14,
  AverageDistanceWithAccess = 15,
  AverageDistanceWithoutAccess = 16,
  MaximumDistance = 17,
  MaximumDistanceWithAccess = 18,
  MaximumDistanceWithoutAccess = 19,
  AverageTime = 20,
  AverageTimeWithAccess = 21,
  AverageTimeWithoutAccess = 22,
  MaximumTime = 23,
  MaximumTimeWithAccess = 24,
  MaximumTimeWithoutAccess = 25,
  AverageDistance2 = 26,
  AverageDistanceWithAccess2 = 27,
  AverageDistanceWithoutAccess2 = 28,
  MaximumDistance2 = 29,
  MaximumDistanceWithAccess2 = 30,
  MaximumDistanceWithoutAccess2 = 31,
  AverageTime2 = 32,
  AverageTimeWithAccess2 = 33,
  AverageTimeWithoutAccess2 = 34,
  MaximumTime2 = 35,
  MaximumTimeWithAccess2 = 36,
  MaximumTimeWithoutAccess2 = 37,
  WithAccessByDistancePercent = 38,
  WithAccessByTimePercent = 39,
  MarketAccessPercent = 40,
  MarketServicing = 41,
  MarketPercent = 42,
  MembershipType = 43,
}

export interface ZipCodeAnalysisSearchParameters {
  state: string;
  county: string;
  city: string;
  zipClass: string;
  zipCode: string;
  specialtyGroupCode: string;
  metPassPercent: boolean;
}

export interface ZipClassAnalysisSearchParameters {
  zipClass: string;
  specialtyGroupCode: string;
  metOverall: boolean;
}

export interface SpecialtyGroupDisplay {
  specialtyGroupCode: string;
  specialtyGroupDescription: string;
}

export interface ZipCodeAnalysisSearchSelections {
  state: DropdownItem<string>;
  county: DropdownItem<string>;
  city: string;
  zipClass: DropdownItem<string>;
  zipCode: string;
  specialtyGroupCode: DropdownItem<string>;
  metPassPercent: DropdownItem<string>;
}

export interface ZipClassAnalysisSearchSelections {
  zipClass: DropdownItem<string>;
  specialtyGroupCode: DropdownItem<string>;
  metOverall: DropdownItem<string>;
}

export interface ZipCodeAnalysisSearchOptions {
  state: Array<DropdownItem<string>>;
  county: Array<DropdownItem<string>>;
  zipClass: Array<DropdownItem<string>>;
  specialtyGroupCode: Array<DropdownItem<string>>;
  zipCode: string[];
  city: string[];
  metPassPercent: Array<DropdownItem<string>>;
}

export interface ZipClassAnalysisSearchOptions {
  zipClass: Array<DropdownItem<string>>;
  specialtyGroupCode: Array<DropdownItem<string>>;
  metOverall: Array<DropdownItem<string>>;
}

export interface ZipCodeAnalysisSearchOptionsResults {
  filters: {
    stateNames: string[];
    counties: string[];
    zipClasses: string[];
    specialtyGroupCodes: SpecialtyGroupDisplay[];
    zipCodes: string[];
    cities: string[];
    metPassPercent: string[];
  };
  projectRunConfigurationId: number;
}

export interface ZipClassAnalysisSearchOptionsResults {
  filters: {
    zipClasses: string[];
    specialtyGroupCodes: SpecialtyGroupDisplay[];
    metOverallOptions: string[];
  };
  projectRunConfigurationId: number;
}

export interface SpecialtyLevelResults {
  [key: string]: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
}

export interface ZipDetailsApiResponse {
  data: ZipCodeDisplayModel[] | ZipClassAggregateResultDisplayModel[];
  hasExpandableSpecialties: boolean;
  projectRunConfigurationId: number;
  resultsFor: string;
  specialtyLevelResults: SpecialtyLevelResults;
  totalNumberOfRecords: number;
}

/**
 * This is the data received from the api endpoint, used to render the table
 */
export interface ZipCodeDisplayModel {
  accessPercentage: number;
  accessTelehealthCredit: number;
  averageDistance: number;
  averageDistance2: number;
  averageDistanceWithAccess: number;
  averageDistanceWithAccess2: number;
  averageDistanceWithoutAccess: number;
  averageDistanceWithoutAccess2: number;
  averageTime: number;
  averageTime2: number;
  averageTimeWithAccess: number;
  averageTimeWithAccess2: number;
  averageTimeWithoutAccess: number;
  averageTimeWithoutAccess2: number;
  city: string;
  county: string;
  isInformationalSpecialty: boolean;
  marketAccessPercent: number;
  marketPercent: number;
  marketPercentage: number;
  marketServicing: number;
  maxDistance: number;
  maxDistance2: number;
  maxDistanceWithAccess: number;
  maxDistanceWithAccess2: number;
  maxDistanceWithoutAccess: number;
  maxDistanceWithoutAccess2: number;
  maxTime: number;
  maxTime2: number;
  maxTimeWithAccess: number;
  maxTimeWithAccess2: number;
  maxTimeWithoutAccess: number;
  maxTimeWithoutAccess2: number;
  memberCountWithAccess: number;
  memberCountWithAccessByDistance: number;
  memberCountWithAccessByTime: number;
  memberCountWithoutAccess: number;
  memberCountWithoutAccessByDistance: number;
  memberCountWithoutAccessByTime: number;
  membershipType: string;
  membershipCount: number;
  metDist: boolean;
  metPassPercent: boolean;
  metTime: boolean;
  minPassPercent: number;
  minProviderCount: number;
  projectRunConfigurationId: number;
  providerCount: number;
  regionSetId: number;
  regionSetSpecialtyGroupId: number;
  reqDist: number;
  reqTime: number;
  servicing: number;
  specialtyCode: string;
  specialtyGroupCode: string;
  specialtyGroupDescription: string;
  stateName: string;
  withAccessPercent: number;
  withDistPercent: number;
  withoutAccessPercent: number;
  withTimePercent: number;
  zipClass: string;
  zipCode: string;
  zipProjectId: number;
}

/**
 * This is the data actually rendered in the table, after making some updates 
 * to the ZipAnalysisDisplayModel fields. Adding hyperlinks, formatting numbers, etc.
 * These keys are in the display order of the table columns.
 */
export interface ZipCodeTableData {
  // this will be the region set specialty group id
  [wafflesKeyColumnName]: string;
  state: string;
  county: string;
  city: string;
  zipClass: string;
  zipCode: JSX.Element;
  isInformationalSpecialty: boolean;
  specialty: JSX.Element | string;
  membershipType: string;
  resultsFor: string;
  minProviderCount: string;
  reqDist: string;
  reqTime: string;
  minPassPercent: string;
  metDist: string;
  metTime: string;
  metPassPercent: string;
  membershipCount: string;
  accessTelehealthCredit: string;
  providerCount: string;
  memberCountWithAccess: string;
  withAccessPercent: string;
  memberCountWithoutAccess: string;
  withoutAccessPercent: string;
  avgDist: string;
  avgDist2: string;
  avgDistWith: string;
  avgDistWithout: string;
  maxDist: string;
  maxDist2: string;
  maxDistWith: string;
  maxDistWithout: string;
  withDistPercent: string;
  avgTime: string;
  avgTime2: string;
  avgTimeWith: string;
  avgTimeWithout: string;
  maxTime: string;
  maxTime2: string;
  maxTimeWith: string;
  maxTimeWithout: string;
  withTimePercent: string;
  avgDistWith2: string;
  avgDistWithout2: string;
  maxDistWith2: string;
  maxDistWithout2: string;
  avgTimeWith2: string;
  avgTimeWithout2: string;
  maxTimeWith2: string;
  maxTimeWithout2: string;
  marketPercent: string;
  marketAccessPercent: string;
  marketServicing: string;
}

export enum ZipProjectModuleRoutesEnum {
  Adequacy = "adequacy",
  Summary = "summary",
}

/**
 * This is the data received from the api for the zip class perspective
 */
export interface ZipClassAggregateResultDisplayModel {
  accessTelehealthCredit: number;
  accessPercentage: number;
  averageDistance: number;
  averageDistance2: number;
  averageDistanceWithAccess: number;
  averageDistanceWithAccess2: number;
  averageDistanceWithoutAccess: number;
  averageDistanceWithoutAccess2: number;
  averageTime: number;
  averageTime2: number;
  averageTimeWithAccess: number;
  averageTimeWithAccess2: number;
  averageTimeWithoutAccess: number;
  averageTimeWithoutAccess2: number;
  isInformationalSpecialty: boolean;
  marketAccessPercent: number;
  marketPercent: number;
  marketPercentage: number;
  marketServicing: number;
  maxDistance: number;
  maxDistance2: number;
  maxDistanceWithAccess: number;
  maxDistanceWithAccess2: number;
  maxDistanceWithoutAccess: number;
  maxDistanceWithoutAccess2: number;
  maxTime: number;
  maxTime2: number;
  maxTimeWithAccess: number;
  maxTimeWithAccess2: number;
  maxTimeWithoutAccess: number;
  maxTimeWithoutAccess2: number;
  memberCountWithAccess: number;
  memberCountWithAccessByDistance: number;
  memberCountWithAccessByTime: number;
  memberCountWithoutAccess: number;
  memberCountWithoutAccessByDistance: number;
  memberCountWithoutAccessByTime: number;
  membershipType: string;
  membershipCount: number;
  metDist: boolean;
  metOverall: boolean;
  metTime: boolean;
  minPassPercent: number;
  minProviderCount: number;
  providerCount: number;
  reqDist: number;
  reqTime: number;
  servicing: number;
  specialtyCode: string;
  specialtyGroupCode: string;
  specialtyGroupDescription: string;
  withAccessPercent: number;
  withDistPercent: number;
  withoutAccessPercent: number;
  withTimePercent: number;
  zipClass: string;
}

/**
 * This is the transformed zip class data displayed in the table.
 * The keys are in the order of the table columns.
 */
export interface ZipClassTableData {
  [wafflesKeyColumnName]: string;
  zipClass: string;
  isInformationalSpecialty: boolean;
  specialty: JSX.Element | string;
  membershipType: string;
  resultsFor: string;
  minProviderCount: string;
  reqDist: string;
  reqTime: string;
  minPassPercent: string;
  membershipCount: string;
  accessTelehealthCredit: string;
  providerCount: string;
  memberCountWith: string;
  withPercent: string;
  memberCountWithout: string;
  withoutPercent: string;
  metOverall: string;
  avgDist: string;
  avgDist2: string;
  avgDistWith: string;
  avgDistWithout: string;
  maxDist: string;
  maxDist2: string;
  maxDistWith: string;
  maxDistWithout: string;
  withDistPercent: string;
  avgTime: string;
  avgTime2: string;
  avgTimeWith: string;
  avgTimeWithout: string;
  maxTime: string;
  maxTime2: string;
  maxTimeWith: string;
  maxTimeWithout: string;
  withTimePercent: string;
  avgDistWith2: string;
  avgDistWithout2: string;
  maxDistWith2: string;
  maxDistWithout2: string;
  avgTimeWith2: string;
  avgTimeWithout2: string;
  maxTimeWith2: string;
  maxTimeWithout2: string;
  metDist: string;
  metTime: string;
  marketPercent: string;
  marketAccessPercent: string;
  marketServicing: string;
}
